"use client"

import { useCallback, useEffect, useRef, useState } from 'react'

import useT from '@hooks/useTranslation'
import ComboboxInput from '@components/form/app-input/combobox/ComboboxInput'
import { findMatch } from '@components/form/app-input/combobox/normalize'
import { ADD_CAR_OFFER_PAGE } from '@constants/routes'

import { LISTING_FROM } from './sellCopy'

import styles from './Sell.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST

const asOptions = (arr) => (arr || []).map((m) => ({ value: m, label: m }))
const fetchHints = (url) => fetch(url).then((r) => { if (!r.ok) throw new Error('net'); return r.json() })
const matchGroups = (q, items) => {
    const hits = items.map((it) => ({ it, m: findMatch(it.label, q) })).filter((x) => x.m)
    if (q) hits.sort((a, b) => a.m.rank - b.m.rank)
    return [{ items: hits.map((x) => x.it) }]
}

// Formularz „Sprzedajesz auto?” pod promowanymi (poniżej 1024 px): marka i model z pełnych list
// formularza dodawania, wysyłka GET do formularza (?marka=&model=). Bez JS zwykłe pola tekstowe.
export default function SellForm () {
    const t = {
        title: useT('Sprzedajesz auto?'),
        text: `${useT('Wybierz markę i model, resztę uzupełnisz w formularzu. Dodaj ogłoszenie od')} ${LISTING_FROM.price} ${useT('na')} ${LISTING_FROM.days} ${useT('dni')}.`,
        marka: useT('Marka'),
        model: useT('Model'),
        marki: useT('Marki'),
        modele: useT('Modele'),
        cta: useT('Dodaj ogłoszenie'),
    }

    const [make, setMake] = useState('')
    const [mounted, setMounted] = useState(false)
    const makeApi = useRef(null), modelApi = useRef(null)
    const liveRef = useRef(null), liveTimer = useRef(null)

    useEffect(() => setMounted(true), [])

    const announce = useCallback((text, delay = 0) => {
        clearTimeout(liveTimer.current)
        liveTimer.current = setTimeout(() => {
            if (!liveRef.current) return
            liveRef.current.textContent = ''
            setTimeout(() => { if (liveRef.current) liveRef.current.textContent = text }, 40)
        }, delay)
    }, [])

    const makeSource = useCallback(() => fetchHints('/api/hints/make_all/car').then(asOptions), [])
    const modelSource = useCallback(() => make
        ? fetchHints(`/api/hints/model_all/car?marka=${encodeURIComponent(make)}`).then(asOptions)
        : Promise.resolve([]), [make])

    const onMakeCommit = useCallback((v) => { setMake(v); modelApi.current?.clear() }, [])

    const onSubmit = (e) => {
        const bad = [makeApi, modelApi].find((api) => api.current && !api.current.commit())
        if (bad) { e.preventDefault(); bad.current.focus() }
    }

    return (
        <form action={HOST + ADD_CAR_OFFER_PAGE} aria-labelledby="sell-h2" className={styles.form} data-home-search method="get" onSubmit={onSubmit}>
            <div className={styles.copy}>
                <h2 className={styles.formTitle} id="sell-h2">{t.title}</h2>
                <p className={styles.formText}>{t.text}</p>
            </div>
            <div className={styles.fields}>
                <ComboboxInput announce={announce} apiRef={makeApi} errorText="Nie udało się wczytać listy marek." groups={matchGroups}
                    emptyHtml={(v) => `<strong>Brak marki „${v}”</strong>Sprawdź pisownię albo usuń część tekstu.`}
                    invalidText={(v) => `Brak marki „${v}”.`}
                    label={t.marka} listLabel={t.marki} loadingText="Wczytywanie listy marek." name="marka"
                    onCommit={onMakeCommit} placeholder="Wybierz" source={makeSource} />

                <ComboboxInput announce={announce} apiRef={modelApi} disabled={mounted && !make}
                    disabledReason="Pole nieaktywne. Najpierw wybierz markę." errorText="Nie udało się wczytać listy modeli." groups={matchGroups}
                    emptyHtml={(v) => `<strong>Brak modelu „${v}”</strong>Sprawdź pisownię albo wybierz z listy.`}
                    invalidText={(v) => `Brak modelu „${v}”.`}
                    label={t.model} listLabel={t.modele} loadingText="Wczytywanie listy modeli." name="model"
                    placeholder="Wybierz" resetKey={make} source={modelSource} />

                <button className={styles.btn} type="submit">{t.cta}</button>
            </div>
            <p aria-live="polite" className={styles.srOnly} ref={liveRef} role="status" />
        </form>
    )
}
